<template>
  <div class="payment-list">
    <div v-if="hasEdit" class="d-flex justify-content-between mb-3">
      <h3 class="page-subtitle">Payment Methods</h3>
      <div>
        <b-button
          class="edit-btn btn--xs"
          variant="outline-secondary"
          v-show="paymentMethods.length > 0"
          @click="isEditing = !isEditing"
          >{{ isEditing ? 'Cancel' : 'Edit' }}</b-button
        >
      </div>
    </div>

    <div v-if="!state.$busy && paymentMethods.length === 0">
      {{
        state.$errorMessage
          ? state.$errorMessage
          : 'There is nothing to display.'
      }}
    </div>

    <div v-if="state.$busy">
      <card-shimmer
        v-for="i in 3"
        :key="i"
        width="100%"
        height="3.75em"
        :rounded="true"
        class="mb-4"
      ></card-shimmer>
    </div>

    <div v-else class="form__section">
      <b-list-group>
        <b-list-group-item
          v-for="paymentMethod in paymentMethods"
          :key="paymentMethod.id"
          :class="
            selectedPaymentMethod &&
            selectedPaymentMethod.id === paymentMethod.id
              ? 'list-group-item--default'
              : !selectedPaymentMethod && paymentMethod.is_default && !isEditing
              ? 'list-group-item--default'
              : ''
          "
          @click="!isEditing ? setDefaultCard(paymentMethod) : ''"
        >
          <b-img
            v-if="paymentMethod.brandIcon"
            :src="paymentMethod.brandIcon"
            width="40"
            height="24"
            class="rounded-0 mr-3"
          ></b-img>
          <b-img
            v-else
            class="rounded-0 mr-3"
            v-bind="mainProps"
            rounded
            alt="Logo"
          ></b-img>
          <span>
            <!-- eslint-disable-next-line -->
            {{ paymentMethod.card.brand | capitalize }} {{ `Ending in ${paymentMethod.card.last4}` }}
          </span>
          <div
            class="remove-link"
            v-show="
              selectedPaymentMethod &&
              selectedPaymentMethod.id === paymentMethod.id
                ? 'list-group-item--default'
                : !selectedPaymentMethod &&
                  paymentMethod.is_default &&
                  !isEditing
                ? 'list-group-item--default'
                : ''
            "
          >
            <b-link>
              <b-icon scale="1.8" icon="check2"></b-icon>
            </b-link>
          </div>
          <div
            class="remove-link"
            @click="onClickRemove(paymentMethod.id)"
            v-show="isEditing"
          >
            <b-link>Remove</b-link>
          </div>
        </b-list-group-item>
      </b-list-group>
    </div>

    <b-modal
      v-model="confirmDelete"
      centered
      class="modal-confirmation text-center p-3"
      hide-footer
      static
    >
      <b-img
        slot="modal-header-close"
        :src="require('@/assets/icons/x-b.svg')"
        width="45"
        height="30"
      ></b-img>

      <div
        class="d-flex flex-column justify-content-between align-items-center text-center"
      >
        <h2>Delete payment method</h2>

        <b-img
          :src="require('@/assets/images/alert.svg')"
          width="150"
          height="150"
          class="modal-confirmation__icon"
        ></b-img>

        <p class="my-4">
          Are you sure you want to permanently delete this payment method?
        </p>
        <div class="modal-confirmation__footer">
          <b-button variant="outline-secondary" @click="confirmDelete = false"
            >Cancel</b-button
          >
          <b-button
            variant="danger"
            :disabled="state.$busy"
            @click="onClickDelete"
          >
            <b-spinner small v-if="state.$busy"></b-spinner>&nbsp;Delete
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-if="hasCreate"
      id="payment-form-modal"
      v-model="show"
      title="Add Payment Method"
      centered
      hide-footer
      size="md"
      static
      class="modal-page modal-form"
      @shown="onModalShown"
    >
      <b-img
        slot="modal-header-close"
        :src="require('@/assets/icons/x.svg')"
        height="24"
      ></b-img>
      <payment-form
        ref="paymentForm"
        @on-success="show = false"
        @on-close="show = false"
      />
    </b-modal>

    <div v-if="hasCreate" class="form__section">
      <b-button class="submit-btn" variant="primary" @click="show = true"
        >Add Payment Method</b-button
      >
    </div>
  </div>
</template>
<script>
/**
 * ==================================================================================
 * Payment List
 * ==================================================================================
 **/

import { mapState, mapActions, mapMutations } from 'vuex'
import Form from '@/utils/form'
import PaymentForm from '@/components/forms/PaymentForm'
import CardShimmer from '@/components/shimmers/shimmer'

export default {
  components: {
    PaymentForm,
    CardShimmer
  },
  props: {
    hasCreate: {
      type: Boolean,
      default: true
    },
    hasEdit: {
      type: Boolean,
      default: true
    },

    /* For selection, the payment method to be displayed as active */
    selectedPaymentMethod: null,
    forSelection: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      paymentMethods: state => state.payment.list
    })
  },
  data: function() {
    return {
      show: false,
      confirmDelete: false,
      isEditing: false,
      current: 0,
      state: new Form({}),
      mainProps: {
        blank: true,
        blankColor: '#00CE7D',
        width: 40,
        height: 24
      }
    }
  },

  methods: {
    ...mapActions({
      getPayments: 'payment/getPayments',
      deletePayment: 'payment/deletePayment',
      setCardAsPrimary: 'payment/setCardAsPrimary',
      addAlert: 'alert/addAlert'
    }),

    ...mapMutations({
      clearPaymentList: 'payment/clearPaymentList'
    }),

    onClickRemove(id) {
      this.current = id
      this.confirmDelete = true
    },

    async onClickDelete() {
      this.state.$setLoading(true)
      await this.deletePayment(this.current).then(() => {
        this.state.$setLoading(false)
        this.confirmDelete = false
        this.addAlert({
          label: 'Payment method has been removed'
        })
      })
    },

    async setDefaultCard(payment) {
      if (this.forSelection) {
        this.$emit('on-select', payment)
      } else {
        this.state.$setLoading(true)
        await this.setCardAsPrimary(payment.id).then(() => {
          this.state.$setLoading(false)
          this.addAlert({
            label: 'Your default payment method has been changed'
          })
        })
      }
    },

    async fetchPayments() {
      this.state.$setLoading(true)
      this.clearPaymentList()
      await this.getPayments()
        .then(() => {
          this.state.$setLoading(false)
        })
        .catch(error => {
          this.state.$handleError(error)
          this.state.$setLoading(false)
        })
        .finally(() => {
          this.$emit('on-fetch')
        })
    },

    onModalShown() {
      this.$refs.paymentForm.reset()
    }
  },

  created() {
    this.fetchPayments()
  }
}
</script>
<style scoped lang="scss">
.payment-list /deep/ {
  .edit-btn {
    width: 130px;
    border-radius: 7px;
    border-width: 1px;
  }

  .remove-link {
    position: absolute;
    top: 12.5px;
    right: 15px;

    > a {
      font-size: 0.95rem;
      font-weight: 600;
      text-decoration: none;
    }
  }

  .submit-btn {
    width: 300px;
  }

  .list-group-item {
    border-radius: 3px;
    margin-bottom: 1.4rem;
    border: 1px solid var(--input-border-color);
  }

  .list-group-item--default {
    border: 1px solid var(--brand-green);
  }

  .modal-form {
    .modal-header {
      padding-bottom: 2rem;

      .close {
        margin-right: -25px;
      }
    }

    .modal-content {
      padding-bottom: 5px;
    }
  }
}

@media (max-width: 568px) {
  .payment-list /deep/ {
    .submit-btn {
      width: 100%;
    }
  }
}
</style>
